import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  Container,
  CssBaseline,
  Grid,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { AccountBalance as WalletIcon } from "@mui/icons-material";
import { useMutation } from "react-query";
import HomeHeader from "../../Components/HomeHeader";
import { lightTheme } from "../../Utils/baseTheme";
import client from "../../API";
import useAuth from "../../Hooks/use-auth";
import { useCreatorAuth } from "../../Hooks/creator-use-auth";
import routes from "../../Config/routes";

const STORAGE_KEY = "claim_funds_token";

const ClaimFundsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthorized: isBrandAuthorized } = useAuth();
  const { isAuthorized: isCreatorAuthorized, isInitialized: isCreatorInitialized } = useCreatorAuth();

  const [claimError, setClaimError] = useState("");
  const [claimed, setClaimed] = useState(null);

  const tokenFromUrl = searchParams.get("token");
  const tokenFromStorage = typeof window !== "undefined" ? sessionStorage.getItem(STORAGE_KEY) : null;
  const claimToken = tokenFromUrl || tokenFromStorage;

  const isAuthenticated = isBrandAuthorized || isCreatorAuthorized;
  const authReady = isCreatorInitialized;

  useEffect(() => {
    if (!claimToken || !authReady) return;
    if (tokenFromUrl && typeof window !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, tokenFromUrl);
    }
  }, [claimToken, tokenFromUrl, authReady]);

  const claimMutation = useMutation(
    (payload) => client.balanceLedger.claimExternalTransfer(payload),
    {
      onSuccess: (data) => {
        setClaimed(data?.transfer || data);
        if (typeof window !== "undefined") {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      },
      onError: (error) => {
        const data = error.response?.data;
        const msg = data?.error || error.message || "Failed to claim funds";
        setClaimError(msg);
        if (error.response?.status === 401) {
          if (typeof window !== "undefined" && claimToken) {
            sessionStorage.setItem(STORAGE_KEY, claimToken);
          }
        }
      },
    }
  );

  const handleClaim = () => {
    if (!claimToken) return;
    setClaimError("");
    claimMutation.mutate({ claim_token: claimToken });
  };

  const handleSignIn = () => {
    if (claimToken) {
      const redirect = `${window.location.origin}/claim-funds?token=${encodeURIComponent(claimToken)}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
    } else {
      navigate(routes.login || "/login");
    }
  };

  const handleSignUp = () => {
    if (claimToken && typeof window !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, claimToken);
    }
    const base = routes.claimSignup || '/claim-signup';
    if (claimToken) {
      navigate(`${base}?type=brand&claim_token=${encodeURIComponent(claimToken)}`);
    } else {
      navigate(`${base}?type=brand`);
    }
  };

  const handleCreatorSignup = () => {
    if (claimToken && typeof window !== 'undefined') {
      sessionStorage.setItem(STORAGE_KEY, claimToken);
    }
    const base = routes.claimSignup || '/claim-signup';
    if (claimToken) {
      navigate(`${base}?type=creator&claim_token=${encodeURIComponent(claimToken)}`);
    } else {
      navigate(`${base}?type=creator`);
    }
  };


  if (!authReady) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Container maxWidth="sm">
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh" }}>
            <CircularProgress />
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (!claimToken) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                Invalid or missing link
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                This claim link is invalid or has expired. If you received an email about claiming funds, use the link from that email.
              </Typography>
              <Button variant="contained" onClick={() => navigate("/")}>
                Go to home
              </Button>
            </CardContent>
          </Card>
        </Container>
      </ThemeProvider>
    );
  }

  if (claimed) {
    const amount = claimed.amount ?? claimed.recipient_new_balance;
    const newBalance = claimed.recipient_new_balance;
    const sender = claimed.sender_username;
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card sx={{ borderRadius: 3, boxShadow: 2, overflow: "hidden" }}>
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <WalletIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Funds claimed!
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main", mb: 2 }}>
                ${typeof amount === "string" ? amount : Number(amount).toFixed(2)}
              </Typography>
              {sender && (
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  From {sender}
                </Typography>
              )}
              {newBalance != null && (
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Your new balance: ${typeof newBalance === "string" ? newBalance : Number(newBalance).toFixed(2)}
                </Typography>
              )}
              <Button variant="contained" onClick={() => navigate("/")}>
                Continue
              </Button>
            </CardContent>
          </Card>
        </Container>
      </ThemeProvider>
    );
  }

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Container maxWidth="sm" sx={{ py: 4 }}>
          <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <WalletIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                You have funds to claim
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Choose what type of Blitz account you want to create, then finish signup with the same email that received this transfer.
              </Typography>

              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Choose your account type
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        height: "100%",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        '&:hover': {
                          borderColor: "primary.main",
                          boxShadow: 2,
                        },
                      }}
                      onClick={handleSignUp}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          Brand account
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          For companies and teams that pay creators and manage campaigns.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        height: "100%",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        '&:hover': {
                          borderColor: "secondary.main",
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleCreatorSignup()}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          Creator account
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          For creators who get paid by brands using Blitz.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <HomeHeader />
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <WalletIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Claim your transfer
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Click below to add these funds to your Blitz balance.
            </Typography>
            {claimError && (
              <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                {claimError}
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              startIcon={claimMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <WalletIcon />}
              onClick={handleClaim}
              disabled={claimMutation.isLoading}
              fullWidth
            >
              {claimMutation.isLoading ? "Claiming..." : "Claim funds"}
            </Button>
          </CardContent>
        </Card>
      </Container>
    </ThemeProvider>
  );
};

export default ClaimFundsPage;
